import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, tokenStore, getApiError } from "@/lib/api";

const AuthContext = createContext(null);

async function loginRequest(path, body, role) {
  try {
    const { data } = await api.post(path, body);
    tokenStore.set(data.token, role);
    return { ok: true, user: data.user };
  } catch (e) {
    return { ok: false, error: getApiError(e) };
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      tokenStore.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const withUser = useCallback(async (promise) => {
    const res = await promise;
    if (res.ok) setUser(res.user);
    return res;
  }, []);

  const loginAdmin = useCallback(
    (email, password) => withUser(loginRequest("/auth/admin/login", { email, password }, "admin")),
    [withUser],
  );

  const loginCustomer = useCallback(
    (mobile, password) => withUser(loginRequest("/auth/customer/login", { mobile, password }, "customer")),
    [withUser],
  );

  const logout = useCallback(() => {
    tokenStore.clear();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, loginAdmin, loginCustomer, logout, refreshMe: fetchMe }),
    [user, loading, loginAdmin, loginCustomer, logout, fetchMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
